// src/constants/browser.const.ts
var BROWSER_NAMES = {
  CHROME: "Chrome",
  FIREFOX: "Firefox",
  SAFARI: "Safari",
  IE: "Internet Explorer",
  EDGE: "Edge",
  OPERA: "Opera"
};

// src/constants/device.const.ts
var DEVICE_TYPES = {
  MOBILE: "mobile",
  TABLET: "tablet",
  DESKTOP: "desktop",
  TV: "tv",
  UNKNOWN: "unknown"
};

// src/constants/os.const.ts
var OS_NAMES = {
  WINDOWS: "Windows",
  MACOS: "macOS",
  IOS: "iOS",
  ANDROID: "Android",
  LINUX: "Linux"
};

// src/constants/reg.const.ts
var REG_EXP = {
  WINDOWS: /Windows/i,
  MACOS: /Mac OS X/i,
  IOS: /(iPhone|iPad|iPod)/i,
  ANDROID: /Android/i,
  LINUX: /Linux/i,
  CHROME: /Chrome/i,
  FIREFOX: /Firefox/i,
  SAFARI: /Safari/i,
  IE: /MSIE|Trident/i,
  TV: /smart-tv|smarttv|googletv|appletv|hbbtv|pov_tv|netcast.tv/i,
  EDGE: /Edge/i,
  OPERA: /Opera|OPR/i,
  MOBILE: /Mobile/i,
  TABLET: /Tablet/i,
  IPAD: /iPad/i,
  IPHONE: /iPhone/i,
  WEBVIEW: /WebView|wv/i
};
var VERSION_PATTERNS = {
  WINDOWS: /Windows NT (\d+\.\d+)/,
  MACOS: /Mac OS X (\d+[._]\d+)/,
  IOS: /OS (\d+_\d+)/,
  ANDROID: /Android (\d+\.\d+)/,
  CHROME: /Chrome\/(\d+\.\d+)/,
  FIREFOX: /Firefox\/(\d+\.\d+)/,
  SAFARI: /Version\/(\d+\.\d+)/,
  IE: /(?:MSIE |rv:)(\d+\.\d+)/
};

// src/constants/index.ts
var UNKNOWN = "Unknown";

// src/utils/browserVersionParser.ts
var parseChromeVersion = (userAgent) => {
  const match = userAgent.match(VERSION_PATTERNS.CHROME);
  return match ? match[1] : UNKNOWN;
};
var parseFirefoxVersion = (userAgent) => {
  const match = userAgent.match(VERSION_PATTERNS.FIREFOX);
  return match ? match[1] : UNKNOWN;
};
var parseSafariVersion = (userAgent) => {
  const match = userAgent.match(VERSION_PATTERNS.SAFARI);
  return match ? match[1] : UNKNOWN;
};
var parseInternetExplorerVersion = (userAgent) => {
  const match = userAgent.match(VERSION_PATTERNS.IE);
  return match ? match[1] : UNKNOWN;
};

// src/detectors/browserDetector.ts
function detectBrowser(userAgent) {
  if (REG_EXP.CHROME.test(userAgent)) {
    return {
      name: BROWSER_NAMES.CHROME,
      version: parseChromeVersion(userAgent)
    };
  }
  if (REG_EXP.FIREFOX.test(userAgent)) {
    return {
      name: BROWSER_NAMES.FIREFOX,
      version: parseFirefoxVersion(userAgent)
    };
  }
  if (REG_EXP.SAFARI.test(userAgent) && !REG_EXP.CHROME.test(userAgent)) {
    return {
      name: BROWSER_NAMES.SAFARI,
      version: parseSafariVersion(userAgent)
    };
  }
  if (REG_EXP.IE.test(userAgent)) {
    return {
      name: BROWSER_NAMES.IE,
      version: parseInternetExplorerVersion(userAgent)
    };
  }
  return {
    name: UNKNOWN,
    version: UNKNOWN
  };
}

// src/detectors/deviceDetector.ts
function detectDevice(userAgent) {
  if (REG_EXP.TABLET.test(userAgent) || REG_EXP.IPAD.test(userAgent)) {
    return { type: DEVICE_TYPES.TABLET };
  }
  if (REG_EXP.MOBILE.test(userAgent)) {
    return { type: DEVICE_TYPES.MOBILE };
  }
  if (REG_EXP.TV.test(userAgent)) {
    return { type: DEVICE_TYPES.TV };
  }
  if (REG_EXP.ANDROID.test(userAgent) && !REG_EXP.MOBILE.test(userAgent)) {
    return { type: DEVICE_TYPES.TABLET };
  }
  return { type: DEVICE_TYPES.DESKTOP };
}

// src/utils/osVersionParser.ts
var parseWindowsVersion = (userAgent) => {
  const match = userAgent.match(/Windows NT (\d+\.\d+)/);
  return match ? match[1] : UNKNOWN;
};
var parseMacOSVersion = (userAgent) => {
  const match = userAgent.match(/Mac OS X (\d+(?:[._]\d+)+)/);
  return match ? match[1].replace(/_/g, ".") : UNKNOWN;
};
var parseIOSVersion = (userAgent) => {
  const match = userAgent.match(/OS (\d+(?:[._]\d+)*)/);
  return match ? match[1].replace(/_/g, ".") : UNKNOWN;
};
var parseAndroidVersion = (userAgent) => {
  const match = userAgent.match(/Android\s+(\d+(?:\.\d+)*)/);
  return match ? match[1] : UNKNOWN;
};

// src/detectors/osDetector.ts
function detectOS(userAgent) {
  if (REG_EXP.WINDOWS.test(userAgent)) {
    return {
      name: OS_NAMES.WINDOWS,
      version: parseWindowsVersion(userAgent)
    };
  }
  if (REG_EXP.IOS.test(userAgent)) {
    return {
      name: OS_NAMES.IOS,
      version: parseIOSVersion(userAgent)
    };
  }
  if (REG_EXP.MACOS.test(userAgent)) {
    return {
      name: OS_NAMES.MACOS,
      version: parseMacOSVersion(userAgent)
    };
  }
  if (REG_EXP.ANDROID.test(userAgent)) {
    return {
      name: OS_NAMES.ANDROID,
      version: parseAndroidVersion(userAgent)
    };
  }
  if (REG_EXP.LINUX.test(userAgent)) {
    return {
      name: OS_NAMES.LINUX,
      version: UNKNOWN
    };
  }
  return {
    name: UNKNOWN,
    version: UNKNOWN
  };
}

// src/detectors/webviewDetector.ts
function detectWebView(userAgent) {
  if (REG_EXP.WEBVIEW.test(userAgent)) {
    return true;
  }
  if (REG_EXP.IOS.test(userAgent) && !REG_EXP.SAFARI.test(userAgent)) {
    return true;
  }
  if (REG_EXP.ANDROID.test(userAgent) && REG_EXP.WEBVIEW.test(userAgent)) {
    return true;
  }
  return false;
}

// src/class/Environment.ts
var Environment = class {
  constructor(userAgent) {
    this.userAgent = userAgent;
    this._env = this.detectEnvironment(userAgent);
  }
  _env;
  detectEnvironment(userAgent) {
    return {
      os: detectOS(userAgent),
      browser: detectBrowser(userAgent),
      device: detectDevice(userAgent),
      isWebView: detectWebView(userAgent)
    };
  }
  get os() {
    return this._env.os;
  }
  get browser() {
    return this._env.browser;
  }
  get device() {
    return this._env.device;
  }
  get isWebView() {
    return this._env.isWebView;
  }
  get isDesktop() {
    return this.device.type === "desktop";
  }
  get isMobile() {
    return this.device.type === "mobile" || this.device.type === "tablet";
  }
  get isWebBrowser() {
    return !this.isWebView;
  }
  toString() {
    return `Environment(OS: ${this.os.name} ${this.os.version}, Browser: ${this.browser.name} ${this.browser.version}, Device: ${this.device.type})`;
  }
};
var Environment_default = Environment;

// src/utils/getEnvirontment.ts
function getEnvironment(userAgent) {
  return new Environment_default(userAgent);
}
export {
  BROWSER_NAMES,
  DEVICE_TYPES,
  Environment_default as Environment,
  OS_NAMES,
  getEnvironment
};
//# sourceMappingURL=index.mjs.map